/**
 * Unified document picker hook.
 *
 * Calls the single GET /docs/picker backend endpoint which returns both
 * wiki documents and Document Hub documents in one paginated query.
 *
 * Sources filter convention (same as before):
 *   "wiki:<category>"  — e.g. "wiki:Laws & Guidance"
 *   "hub:<key>"        — hub:library | hub:organisation
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from 'src/lib/api/axios';
import { DocumentPickerRow, DocumentPickerFilterDef, DocumentPickerFilters } from './DocumentPickerContent';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const SOURCES_FILTER_KEY = 'sources';
export const WIKI_SOURCE_PREFIX = 'wiki:';

const PAGE_SIZE = 10;

const HUB_SOURCE_OPTIONS = [
  { value: 'hub:library', label: 'Personal Folder' },
  { value: 'hub:organisation', label: 'Organisation Folder' }
];

// ---------------------------------------------------------------------------
// API types
// ---------------------------------------------------------------------------

interface DocumentPickerItem {
  id: string;
  title: string;
  filename: string | null;
  modified_at: string | null;
  source: 'wiki' | 'hub';
  source_label: string | null;
}

interface DocumentPickerResponse {
  items: DocumentPickerItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface FilterAggregations {
  category_counts: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

async function fetchPickerDocumentsByIds(ids: string[]): Promise<DocumentPickerResponse> {
  const p = new URLSearchParams();
  p.set('page_size', String(ids.length));
  ids.forEach((id) => p.append('ids', id));
  const res = await apiGet<DocumentPickerResponse>(`docs/picker?${p.toString()}`, {
    meta: { globalError: false, context: 'picker-titles' }
  });
  return res.data;
}

async function fetchPickerDocuments(params: {
  page: number;
  search?: string;
  sources?: string[];
}): Promise<DocumentPickerResponse> {
  const p = new URLSearchParams();
  p.set('page', String(params.page));
  p.set('page_size', String(PAGE_SIZE));
  if (params.search) p.set('search', params.search);
  (params.sources ?? []).forEach((s) => p.append('sources', s));
  const res = await apiGet<DocumentPickerResponse>(`docs/picker?${p.toString()}`, {
    meta: { globalError: false, context: 'doc-picker' }
  });
  return res.data;
}

async function fetchAggregations(): Promise<FilterAggregations> {
  const res = await apiGet<FilterAggregations>('docs/filter-counts', {
    meta: { globalError: false, context: 'doc-picker-agg' }
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseDocumentPickerResult {
  rows: DocumentPickerRow[];
  isLoading: boolean;
  filterDefs: DocumentPickerFilterDef[];
  page: number;
  pageCount: number;
  totalCount: number;
  setPage: (page: number) => void;
}

export function useDocumentPicker(filters: DocumentPickerFilters): UseDocumentPickerResult {
  const [page, setPage] = useState(1);

  // Debounce search so we don't fire a request on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(id);
  }, [filters.search]);

  // Reset to page 1 whenever debounced search or active filters change
  const filterKey = debouncedSearch + JSON.stringify(filters.active);
  useEffect(() => {
    setPage(1);
  }, [filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeSources = filters.active[SOURCES_FILTER_KEY] ?? [];

  // ── Aggregations (for wiki category options in the Sources dropdown) ──────
  const { data: aggregationsData } = useQuery({
    queryKey: ['doc-picker-aggregations'],
    queryFn: fetchAggregations,
    staleTime: 5 * 60_000
  });

  // ── Documents ─────────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['doc-picker', debouncedSearch, page, activeSources],
    queryFn: () =>
      fetchPickerDocuments({
        page,
        search: debouncedSearch || undefined,
        sources: activeSources.length ? activeSources : undefined
      }),
    staleTime: 60_000,
    placeholderData: (prev) => prev
  });

  // ── Map API items → DocumentPickerRow ──────────────────────────────────────
  const rows: DocumentPickerRow[] = useMemo(
    () =>
      (data?.items ?? []).map((item) => ({
        id: item.id,
        name: item.source === 'wiki' ? item.title : (item.filename ?? item.title),
        filename: item.filename ?? undefined,
        updatedAt: item.modified_at ?? undefined,
        badge:
          item.source === 'wiki'
            ? { label: item.source_label ?? 'Wiki', className: 'bg-blue-50 text-blue-600' }
            : { label: 'Document Hub', className: 'bg-purple-50 text-purple-600' }
      })),
    [data]
  );

  // ── Sources filter definition ──────────────────────────────────────────────
  const wikiSourceOptions = useMemo(() => {
    const cats = Object.keys(aggregationsData?.category_counts ?? {});
    return cats.map((cat) => ({ value: `${WIKI_SOURCE_PREFIX}${cat}`, label: cat }));
  }, [aggregationsData]);

  const filterDefs: DocumentPickerFilterDef[] = useMemo(
    () => [
      {
        key: SOURCES_FILTER_KEY,
        label: 'Sources',
        options: [...wikiSourceOptions, ...HUB_SOURCE_OPTIONS],
        sections: [
          { label: 'Regulatory Wiki', options: wikiSourceOptions },
          { label: 'Document Hub', options: HUB_SOURCE_OPTIONS }
        ]
      }
    ],
    [wikiSourceOptions]
  );

  const totalCount = data?.total ?? 0;
  const pageCount = Math.max(1, data?.total_pages ?? 1);

  return { rows, isLoading, filterDefs, page, pageCount, totalCount, setPage };
}

// ---------------------------------------------------------------------------
// usePickerTitles — resolve a list of document IDs → { id, title } records.
// Used to show titles for pinned documents immediately after selection,
// without waiting for the specialist to be re-fetched.
// ---------------------------------------------------------------------------

export interface PickerTitleRef {
  id: string;
  title: string;
  filename?: string;
}

export function usePickerTitles(ids: string[]): PickerTitleRef[] {
  const key = ids.slice().sort().join(',');
  const { data } = useQuery({
    queryKey: ['picker-titles', key],
    queryFn: () => fetchPickerDocumentsByIds(ids),
    enabled: ids.length > 0,
    staleTime: 60_000
  });
  return useMemo(
    () =>
      (data?.items ?? []).map((item) => ({
        id: item.id,
        title: item.source === 'wiki' ? item.title : (item.filename ?? item.title),
        filename: item.filename ?? undefined
      })),
    [data]
  );
}
